/*
  Phase 2 loading

  Phase 1 is the first screen: the high-priority image (PDP gallery, hero banner) gets the bandwidth on its own.
  As soon as it has loaded (or the page "load" event fired, whichever comes first) the rest of the page's images
  load in the background, in page order and two at a time, instead of waiting until the visitor scrolls near them.
  Skipped on Data Saver and 2G.

  Also:
  - restores the product gallery images that product-gallery.liquid renders without src (data-evo-src) so they
    do not compete with the first image; any tap/key press restores them at once
  - [data-evo-near-load] containers load their lazy images ~800px before they scroll into view. For images that
    only move into view through CSS transforms (logo marquee), which iOS Safari never lazy-loads.
*/
(() => {
  if (window.__evoPhase2) return;
  window.__evoPhase2 = true;

  const connection = navigator.connection || {};
  const lean = connection.saveData === true || /(^|-)2g$/.test(connection.effectiveType || '');
  const hasBox = (el) => el.getClientRects().length > 0;

  const restore = () => {
    document.querySelectorAll('img[data-evo-src]').forEach((img) => {
      if (img.dataset.evoSrcset) img.srcset = img.dataset.evoSrcset;
      img.src = img.dataset.evoSrc;
      img.removeAttribute('data-evo-src');
      img.removeAttribute('data-evo-srcset');
      // visible slides load now; hidden variant slides keep lazy and load when shown (or in the queue below)
      if (hasBox(img)) img.loading = 'eager';
    });
  };
  ['pointerdown', 'touchstart', 'keydown'].forEach((type) =>
    document.addEventListener(type, restore, { capture: true, passive: true })
  );

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
          observer.unobserve(entry.target);
        }),
      { rootMargin: '800px 0px' }
    );
    document.querySelectorAll('[data-evo-near-load]').forEach((el) => observer.observe(el));
  }

  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    restore();
    if (lean) return;

    const pending = (img) => !img.complete && (img.getAttribute('src') || img.getAttribute('srcset'));
    const lazy = Array.from(document.querySelectorAll('img[loading="lazy"]')).filter(pending);
    // visible images in page order first, then hidden gallery slides (other flavours) so a flavour switch is instant
    const queue = [
      ...lazy.filter(hasBox),
      ...lazy.filter((img) => !hasBox(img) && img.closest('.product-gallery__media')),
    ];

    let active = 0;
    const next = () => {
      while (active < 2 && queue.length) {
        const img = queue.shift();
        if (img.complete) continue;
        active++;
        let finished = false;
        const done = () => {
          if (finished) return;
          finished = true;
          active--;
          next();
        };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
        setTimeout(done, 10000);
        img.fetchPriority = 'low';
        img.loading = 'eager';
      }
    };
    next();
  };

  const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 200));
  const startSoon = () => idle(start);

  const hero = Array.from(document.querySelectorAll('img[fetchpriority="high"]')).find(hasBox);
  if (hero && !hero.complete) {
    hero.addEventListener('load', startSoon, { once: true });
    hero.addEventListener('error', startSoon, { once: true });
  } else if (hero) {
    startSoon();
  }
  if (document.readyState === 'complete') startSoon();
  else window.addEventListener('load', startSoon, { once: true });
})();
